import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../store';
import { authorizedOfficerActions } from '../store/authorizedOfficer';
import { FlowStage, flowActions } from '../store/flow';
import { userActions, Person, User } from '../store/user';
import { OneIdentityService } from '../service/oneIdentityService';
import MessageBanner from './MessageBanner';
import { StorageService } from '../helper/storage-service';
import { Permissions } from '../store/permissions';
const { COMPANY_INFORMATION_URL, EMAIL_REGEX, NAME_PATTERN_REGEX, ERROR_URL } = require('../helper/application-conf').default;

const {
  Label, Input, PhoneInput, RadioButton, RadioGroup, ButtonPrimary, Alert, Tooltip, TooltipButton, GlyphInfo, dlsBrightBlue,
} = require('@americanexpress/dls-react');

const UserInformation = (props: { aoEmailDuplicate: any; oneIdentityEnabled:any;}) => {
  const appDispatch = useAppDispatch();
  const authorizedOfficerValue: string = useAppSelector((state) => (state.user.authorizedOfficer ? 'yes' : 'no'));
  const user: User = useAppSelector((state) => state.user);
  const authorizedOfficer: Person = useAppSelector((state) => state.authorizedOfficer);
  const permissions: Permissions = useAppSelector((state) => state.permissions);

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const oneIdentityService = new OneIdentityService();
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation();
  const genericError = t('genericError');
  const navigate = useNavigate();
  const SITENAME = window.location.pathname.split('/')[2];
  const storage = new StorageService();

  const { aoEmailDuplicate, oneIdentityEnabled } = props;


  useEffect(() => {
     if (oneIdentityEnabled) {
      appDispatch(userActions.setUser({
        ...user,
        cellPhone: permissions.cellphone || ''
      }));
    }
    appDispatch(flowActions.setStage(FlowStage.UserInformation));
  }, [appDispatch]);

  const updateField = useCallback((field: string, value: string, e: any = undefined) => {
    if (e && e.target.getAttribute('numeric') === 'true' && e.target.value.match(/\D/)) {
      return;
    }
    appDispatch(userActions.setUser({
      ...user,
      [field]: value,
    }));
  }, [user, appDispatch]);

  const setAuthorizedOfficerValue = useCallback((value: string) => {
    appDispatch(userActions.setUser({
      ...user,
      authorizedOfficer: value === 'yes',
    }));
  }, [user, appDispatch]);

  const updateAOField = useCallback((field: string, value: string, e: any = undefined) => {
    if (e && e.target.getAttribute('numeric') === 'true' && e.target.value.match(/\D/)) {
      return;
    }
    appDispatch(authorizedOfficerActions.setAuthorizedOfficer({
      ...authorizedOfficer,
      [field]: value,
    }));
  }, [authorizedOfficer, appDispatch]);

  const validateAO = useCallback((invalid: any) => {
    if (authorizedOfficerValue === 'no') {
      if (authorizedOfficer.email.length === 0) {
        invalid.push('ao-email-required');
      }
      if ((authorizedOfficer.email.length > 0 && !authorizedOfficer.email.match(EMAIL_REGEX)) || authorizedOfficer.email.length > 254) {
        invalid.push('ao-email');
      }
      if (authorizedOfficer.email.length > 0 && authorizedOfficer.email === user.email) {
        invalid.push('ao-email-duplicate');
      }
      if (authorizedOfficer.firstName.length === 0) {
        invalid.push('ao-first-required');
      }
      if ((authorizedOfficer.firstName !== '' && !authorizedOfficer.firstName.match(NAME_PATTERN_REGEX)) || authorizedOfficer.firstName.length > 19) {
        invalid.push('ao-first');
      }
      if (authorizedOfficer.lastName.length === 0) {
        invalid.push('ao-last-required');
      }
      if ((authorizedOfficer.lastName !== '' && !authorizedOfficer.lastName.match(NAME_PATTERN_REGEX)) || authorizedOfficer.lastName.length > 20) {
        invalid.push('ao-last');
      }
      if (authorizedOfficer.cellPhone.length < 10) {
        invalid.push('ao-cellPhone');
      }
      if(authorizedOfficer.jobTitle.length > 40) {
        invalid.push('job-ao-length');
      }
      if (authorizedOfficer.businessPhone.length > 0 && authorizedOfficer.businessPhone.length < 10) {
        invalid.push('ao-businessPhone');
      }
      if(authorizedOfficer.extension.length > 10) {
        invalid.push('ao-extension-length');
      }
    }
    return invalid;
  }, [authorizedOfficerValue, authorizedOfficer, user]);
  const invalidFields = useCallback((submitting = false) => {
    if (!hasSubmitted && !submitting) {
      return [];
    }
    const invalid = [];
    if (user.firstName.length === 0 || user.firstName.length > 19 || !user.firstName.match(NAME_PATTERN_REGEX)) {
      invalid.push('firstName');
    }
    if (user.firstName !== '' && !user.firstName.match(NAME_PATTERN_REGEX)) {
      invalid.push('firstName-regex');
    }
    if (user.lastName.length === 0 || user.lastName.length > 20 || !user.lastName.match(NAME_PATTERN_REGEX)) {
      invalid.push('lastName');
    }
    if (user.lastName !== '' && !user.lastName.match(NAME_PATTERN_REGEX)) {
      invalid.push('lastName-regex');
    }
    if (user.jobTitle && user.jobTitle.length > 30) {
      invalid.push('jobTitle');
    }
     if (!oneIdentityEnabled && user.cellPhone.length < 10) {
      invalid.push('cellPhone');
    }
    if (oneIdentityEnabled && (!permissions.cellphone || permissions.cellphone.length < 10)) {
      invalid.push('cellPhone');
    }
    if (user.businessPhone.length > 0 && user.businessPhone.length < 10) {
      invalid.push('businessPhone');
    }
    if(user.extension.length > 10) {
        invalid.push('extension-length');
      }
    if (authorizedOfficerValue === 'no') {
      const invalidFieldsAO = validateAO(invalid);
      invalid.join(invalidFieldsAO);
    }
    return invalid;
  }, [user, hasSubmitted, authorizedOfficerValue, validateAO]);

  const fieldStatusToDisplay = useCallback((field: any) => {
    if (invalidFields().some((item) => { return field.includes(item); })) {
      return 'error';
    }
    return 'default';
  }, [invalidFields]);

  const fieldStatus = useCallback((field: string) => {
    if (invalidFields().includes(field)) {
      return 'error';
    }
    return 'default';
  }, [invalidFields]);

  const next = useCallback(() => {
    setHasSubmitted(true);
    if (invalidFields(true).length === 0) {
      appDispatch(flowActions.setStage(FlowStage.CompanyInformation));
      navigate(COMPANY_INFORMATION_URL);
    }
  }, [appDispatch, invalidFields, navigate]);

  return (
    <div className="container">
      <div className="row pad-t">
        <h5 className="col-md-offset-2 col-md-8 heading-5">{t('user_info.header')}</h5>
      </div>
      <div className="row pad-t">
        <h4 className="col-md-offset-2 col-md-8 heading-4">{t('user_info.system_administrator_header')}</h4>
      </div>
      <div className="row pad-t">
        <p className="col-md-offset-2 col-md-8 body-1">{t('user_info.system_administrator_description')}</p>
      </div>
      <form>
        <div className="row pad-t">
          <div className="col-md-offset-2 col-md-4">
            <Label id="first-name-label" htmlFor="first-name-input">
              {t('user_info.first_name')}
            </Label>
            <Input id="first-name-input" maxLength={19} value={user.firstName} required={true} disabled={true} />
            {(fieldStatus('firstName') === 'error' || fieldStatus('firstName-regex') === 'error')
              && <Alert className="pad-1-t">{t('errors.invalid_fname')}</Alert>}
          </div>
          <div className="col-md-4">
            <Label id="last-name-label" htmlFor="last-name-input">
              {t('user_info.last_name')}
            </Label>
            <Input id="last-name-input" maxLength={20} value={user.lastName} required={true} disabled={true} />
            {(fieldStatus('lastName') === 'error' || fieldStatus('lastName-regex') === 'error')
              && <Alert className="pad-1-t">{t('errors.invalid_lname')}</Alert>}
          </div>
        </div>
        <div className="row pad-t">
          <div className="col-md-offset-2 col-md-4">
            <Label id="job-label" htmlFor="job-input">
              {t('user_info.job_title')}
            </Label>
            <Input id="job-input" maxLength={30} value={user.jobTitle} onChange={(e: any) => updateField('jobTitle', e.target.value)} />
            {fieldStatus('jobTitle') === 'error'
              && <Alert className="pad-1-t">{t('errors.invalid_jobTitle')}</Alert>}
          </div>
          <div className="col-md-4">
            <Label id="business-email-label" htmlFor="business-email-input">
              {t('user_info.business_email')}
            </Label>
            <Input id="business-email-input" maxLength={254} value={user.email} required={true} disabled={true} />
          </div>
        </div>
        
        <div className="row pad-t">
          <div className="col-md-offset-2 col-md-4">
            <Label id="cell-phone-label" htmlFor="cell-phone-input">
              {t('user_info.mobile_phone')}
            </Label>
            <PhoneInput id="cell-phone-input" value={oneIdentityEnabled ? permissions.cellphone : user.cellPhone} status={fieldStatus('cellPhone')} onChange={(e: any) => updateField('cellPhone', e.target.value)} showFlag={true} showCallingCode={true} callingCode="1" phoneFormat="(###) ###-####" 
              disabled={oneIdentityEnabled ? true : false}/>
            {fieldStatus('cellPhone') === 'error'
              && <Alert className="pad-1-t">{t('errors.invalid_phone_number')}</Alert>}
          </div>
        </div>
      
        <div className="row pad-t">
          <div className="col-md-offset-2 col-md-4">
            <Label id="business-phone-label" htmlFor="business-phone-input">
              {t('user_info.work_phone')}
            </Label>
            <PhoneInput id="business-phone-input" value={user.businessPhone} status={fieldStatus('businessPhone')} onChange={(e: any) => updateField('businessPhone', e.target.value)} showFlag={true} showCallingCode={true} callingCode="1" phoneFormat="(###) ###-####" />
            {fieldStatus('businessPhone') === 'error'
              && <Alert className="pad-1-t">{t('errors.invalid_work_number')}</Alert>}
          </div>
          <div className="col-md-4">
            <Label id="extension-label" htmlFor="extension-input">
              {t('user_info.extension')}
            </Label>
            <Input id="extension-input" maxLength={10} numeric="true" value={user.extension} onChange={(e: any) => updateField('extension', e.target.value, e)} required={false} />
            {fieldStatus('extension-length') === 'error'
              && <Alert className="pad-1-t">{t('errors.ten_characters_length_error')}</Alert>}
          </div>
        </div>
        <div className="row pad-t">
          <div className="col-md-offset-2 col-md-8">
            <p className="label-3">{t('user_info.terms_consent_preamble')} <a className="text-underline" href={t('user_info.terms_link_url')!} target="_blank" rel="noreferrer">{t('user_info.terms_link_text')}</a> {t('user_info.terms_link_epilogue')}</p>
          </div>
        </div>
        <div className="row">
          <div className="col-md-offset-2 col-md-8">
            <RadioGroup defaultChecked={authorizedOfficerValue} value={authorizedOfficerValue} onChange={(e: any) => setAuthorizedOfficerValue(e.target.value)}>
              <RadioButton
                className="display-inline-block"
                id="radio-yes"
                label={t('simple_affirmative')}
                value="yes"
              />
              <RadioButton
                className="display-inline-block"
                id="radio-no"
                label={t('simple_negative')}
                value="no"
              />
            </RadioGroup>
          </div>
        </div>
        {authorizedOfficerValue === 'no' && (
          <div>
            <div className="row pad-t">
              <h4 className="col-md-offset-2 col-md-8 heading-4">
                <span id="tooltip-data">{t('user_info.authorized_officer_header')}</span>
                <Tooltip aria-labelledby="tooltip-data" info={true} placement="right" triggerElement={<TooltipButton info={true} icon={<GlyphInfo theme={dlsBrightBlue} size="lg" />} />}>
                  <p className="body-1 pad-1-t">{t('user_info.ao_tooltip')}</p>
                </Tooltip>
              </h4>
            </div>
            <div className="row pad-t">
              <p className="col-md-offset-2 col-md-8 body-1">{t('user_info.terms_explainer')}</p>
              <p className="col-md-offset-2 col-md-8 body-1 pad-1-t">{t('user_info.terms_alternative')}</p>
            </div>
            <div className="row pad-t">
              <div className="col-md-offset-2 col-md-4">
                <Label id="first-name-ao-label" htmlFor="first-name-ao-input">
                  {t('user_info.first_name')}
                </Label>
                <Input id="first-name-ao-input" maxLength={19} value={authorizedOfficer.firstName} onChange={(e: any) => updateAOField('firstName', e.target.value)} status={fieldStatusToDisplay(['ao-first', 'ao-first-required'])} required={true} />
                {fieldStatus('ao-first-required') === 'error'
                  && <Alert className="pad-1-t">{t('errors.firstname_required_field')}</Alert>}
                {(fieldStatus('ao-first') === 'error')
                  && <Alert className="pad-1-t">{t('errors.invalid_fname')}</Alert>}
              </div>
              <div className="col-md-4">
                <Label id="last-name-ao-label" htmlFor="last-name-ao-input">
                  {t('user_info.last_name')}
                </Label>
                <Input id="last-name-ao-input" maxLength={20} value={authorizedOfficer.lastName} onChange={(e: any) => updateAOField('lastName', e.target.value)} status={fieldStatusToDisplay(['ao-last', 'ao-last-required'])} required={true} />
                {fieldStatus('ao-last-required') === 'error'
                  && <Alert className="pad-1-t">{t('errors.lastname_required_field')}</Alert>}
                {(fieldStatus('ao-last') === 'error')
                  && <Alert className="pad-1-t">{t('errors.invalid_lname')}</Alert>}
              </div>
            </div>
            <div className="row pad-t">
              <div className="col-md-offset-2 col-md-4">
                <Label id="job-ao-label" htmlFor="job-ao-input">
                  {t('user_info.job_title')}
                </Label>
                <Input id="job-ao-input" maxLength={40} value={authorizedOfficer.jobTitle} onChange={(e: any) => updateAOField('jobTitle', e.target.value)} />
                {(fieldStatus('job-ao-length') === 'error')
                  && <Alert className="pad-1-t">{t('errors.forty_characters_length_error')}</Alert>}
              </div>
              <div className="col-md-4">
                <Label id="business-email-ao-label" htmlFor="business-email-ao-input">
                  {t('user_info.business_email')}
                </Label>
                <Input id="business-email-ao-input" maxLength={254} value={authorizedOfficer.email} onChange={(e: any) => updateAOField('email', e.target.value)} status={fieldStatusToDisplay(['ao-email', 'ao-email-required', 'ao-email-duplicate'])} required={true} />
                {fieldStatus('ao-email') === 'error'
                  && <Alert className="pad-1-t">{t('errors.email_invalid')}</Alert>}
                {fieldStatus('ao-email-required') === 'error'
                  && <Alert className="pad-1-t">{t('errors.email_required')}</Alert>}
                {((fieldStatus('ao-email-duplicate') === 'error') || aoEmailDuplicate)
                  && <Alert className="pad-1-t">{t('errors.email_duplicate')}</Alert>}
              </div>
            </div>
            <div className="row pad-t">
              <div className="col-md-offset-2 col-md-4">
                <Label id="cell-phone-ao-label" htmlFor="cell-phone-ao-input">
                  {t('user_info.mobile_phone')}
                </Label>
                <PhoneInput id="cell-phone-ao-input" value={authorizedOfficer.cellPhone} onChange={(e: any) => updateAOField('cellPhone', e.target.value)} status={fieldStatus('ao-cellPhone')} showFlag={true} showCallingCode={true} callingCode="1" phoneFormat="(###) ###-####" />
                {fieldStatus('ao-cellPhone') === 'error'
                  && <Alert className="pad-1-t">{t('errors.invalid_phone_number')}</Alert>}
              </div>
            </div>
            <div className="row pad-t">
              <div className="col-md-offset-2 col-md-4">
                <Label id="business-phone-ao-label" htmlFor="business-phone-ao-input">
                  {t('user_info.work_phone')}
                </Label>
                <PhoneInput id="business-phone-ao-input" value={authorizedOfficer.businessPhone} onChange={(e: any) => updateAOField('businessPhone', e.target.value)} status={fieldStatus('ao-businessPhone')} showFlag={true} showCallingCode={true} callingCode="1" phoneFormat="(###) ###-####" />
                {fieldStatus('ao-businessPhone') === 'error'
                  && <Alert className="pad-1-t">{t('errors.invalid_work_number')}</Alert>}
              </div>
              <div className="col-md-4">
                <Label id="extension-ao-label" htmlFor="extension-ao-input">
                  {t('user_info.extension')}
                </Label>
                <Input id="extension-ao-input" maxLength={10} numeric="true" value={authorizedOfficer.extension} onChange={(e: any) => updateAOField('extension', e.target.value, e)} required={false} />
                {fieldStatus('ao-extension-length') === 'error'
              && <Alert className="pad-1-t">{t('errors.ten_characters_length_error')}</Alert>}
              </div>
            </div>
          </div>
        )}
        <div className="row pad-4-t pad-4-b">
          <h4 className="col-md-4 heading-4 col-md-offset-5">
            <ButtonPrimary size="md" onClick={next}>{t('continue')}</ButtonPrimary>
          </h4>
        </div>
      </form>
    </div>
  );
};

export default UserInformation;
